import React from 'react';
import { Card, Typography, Row, Col, List, Tag } from 'antd';
import { BookOutlined, DatabaseOutlined, CodeOutlined, RocketOutlined, StarOutlined } from '@ant-design/icons';
import './Research.less';

const { Title, Paragraph } = Typography;

interface ResearchProject {
  id: number;
  name: string;
  description: string;
  category: string;
  funding: string;
  year: string;
  status: string;
  keywords: string[];
  imageUrl?: string;
}

const Research: React.FC = () => {
  // 模拟科研项目数据
  const researchProjects: ResearchProject[] = [
    {
      id: 1,
      name: '新一代人工智能算法在智能交通中的应用研究',
      description: '本项目致力于开发适用于复杂交通环境的人工智能算法，包括交通流量预测、智能信号控制和自动驾驶辅助系统等核心技术。',
      category: '国家重点研发计划',
      funding: '1200万元',
      year: '2023-2026',
      status: '进行中',
      keywords: ['人工智能', '智能交通', '机器学习', '计算机视觉'],
      imageUrl: 'https://picsum.photos/seed/research1/600/400'
    },
    {
      id: 2,
      name: '6G移动通信网络关键技术研究',
      description: '研究面向6G的太赫兹通信、智能超表面、分布式MIMO等关键技术，构建高速、低时延、广覆盖的下一代移动通信网络架构。',
      category: '国家自然科学基金重点项目',
      funding: '800万元',
      year: '2022-2025',
      status: '进行中',
      keywords: ['6G', '太赫兹通信', '智能超表面', 'MIMO'],
      imageUrl: 'https://picsum.photos/seed/research2/600/400'
    },
    {
      id: 3,
      name: '面向数字孪生的工业互联网平台研发',
      description: '开发基于数字孪生技术的工业互联网平台，实现工业设备的实时监测、故障诊断和预测性维护，提升工业生产效率。',
      category: '省部级重点项目',
      funding: '350万元',
      year: '2023-2024',
      status: '进行中',
      keywords: ['数字孪生', '工业互联网', '物联网', '故障诊断'],
      imageUrl: 'https://picsum.photos/seed/research3/600/400'
    },
    {
      id: 4,
      name: '新型量子计算算法及其应用研究',
      description: '探索量子计算的新算法和新模型，重点研究量子机器学习、量子化学模拟等领域的应用，推动量子计算实用化进程。',
      category: '国家自然科学基金面上项目',
      funding: '65万元',
      year: '2022-2025',
      status: '进行中',
      keywords: ['量子计算', '量子算法', '量子机器学习', '量子化学'],
      imageUrl: 'https://picsum.photos/seed/research4/600/400'
    },
    {
      id: 5,
      name: '网络空间安全主动防御技术研究',
      description: '研究网络空间威胁检测、攻击溯源和主动防御技术，构建多层次、全方位的网络安全防护体系。',
      category: '国家重点研发计划',
      funding: '900万元',
      year: '2022-2025',
      status: '进行中',
      keywords: ['网络安全', '威胁检测', '攻击溯源', '主动防御'],
      imageUrl: 'https://picsum.photos/seed/research5/600/400'
    },
    {
      id: 6,
      name: '基于区块链的数字身份认证与隐私保护系统',
      description: '构建基于区块链技术的去中心化数字身份认证系统，实现用户数据的隐私保护和安全共享。',
      category: '省部级项目',
      funding: '120万元',
      year: '2023-2024',
      status: '进行中',
      keywords: ['区块链', '数字身份', '隐私保护', '零知识证明'],
      imageUrl: 'https://picsum.photos/seed/research6/600/400'
    }
  ];

  // 研究方向数据
  const researchDirections = [
    {
      name: '人工智能与机器学习',
      description: '重点研究深度学习、强化学习、联邦学习等前沿技术，以及在智能推荐、计算机视觉、自然语言处理等领域的应用。',
      icon: <CodeOutlined />
    },
    {
      name: '通信与网络技术',
      description: '研究5G/6G通信、物联网、边缘计算、软件定义网络等技术，构建下一代智能通信网络架构。',
      icon: <DatabaseOutlined />
    },
    {
      name: '量子信息科学',
      description: '探索量子计算、量子通信、量子密码等量子信息科学前沿领域，推动量子技术的基础研究和应用探索。',
      icon: <BookOutlined />
    },
    {
      name: '先进计算架构',
      description: '研究新型计算架构、芯片设计、高性能计算等技术，提升计算系统的性能、能效和可靠性。',
      icon: <RocketOutlined />
    }
  ];

  return (
    <div className="research-container">
      <Title level={2}>科研平台</Title>
      
      {/* 科研概况 */}
      <Card className="overview-card">
        <Title level={4}>科研概况</Title>
        <Paragraph>
          羊小羊高度重视科学研究工作，致力于开展面向国家战略需求和国际学术前沿的基础研究和应用研究。
          近年来，学校承担了大量国家级和省部级科研项目，取得了一系列具有重要影响的科研成果。
          学校拥有多个国家级和省部级科研平台，为科学研究提供了良好的支撑条件。
        </Paragraph>
        <Paragraph>
          2023年度，学校共承担各类科研项目320余项，其中包括国家重点研发计划、国家自然科学基金、
          国家社会科学基金等国家级项目80余项，省部级项目120余项。年度科研经费超过2亿元，
          发表高水平学术论文400余篇，获得授权发明专利150余项。
        </Paragraph>
      </Card>

      {/* 重点科研项目 */}
      <div className="key-projects-section">
        <div className="section-title-with-icon">
          <StarOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          <Title level={3} style={{ margin: 0 }}>重点科研项目</Title>
        </div>
        <Row gutter={[24, 24]}>
          {researchProjects.map(project => (
            <Col xs={24} sm={12} lg={8} key={project.id}>
              <Card 
                className="project-card"
                cover={project.imageUrl ? <img alt={project.name} src={project.imageUrl} /> : null}
                extra={<Tag color={project.status === '进行中' ? 'green' : 'blue'}>{project.status}</Tag>}
              >
                <Title level={5} className="project-name">{project.name}</Title>
                <div className="project-meta">
                  <Tag color="blue">{project.category}</Tag>
                  <Tag color="orange">{project.funding}</Tag>
                  <Tag color="purple">{project.year}</Tag>
                </div>
                <Paragraph ellipsis={{ rows: 3 }} className="project-description">
                  {project.description}
                </Paragraph>
                <div className="project-keywords">
                  {project.keywords.map((keyword, index) => (
                    <Tag key={index} color="default" className="keyword-tag">{keyword}</Tag>
                  ))}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* 研究方向 */}
      <div className="research-directions-section">
        <Title level={3}>主要研究方向</Title>
        <Row gutter={[24, 24]}>
          {researchDirections.map((direction, index) => (
            <Col xs={24} sm={12} key={index}>
              <Card className="direction-card" hoverable>
                <div className="direction-header">
                  <div className="direction-icon">{direction.icon}</div>
                  <Title level={4} className="direction-name">{direction.name}</Title>
                </div>
                <Paragraph className="direction-description">
                  {direction.description}
                </Paragraph>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* 科研成果统计 */}
      <Card className="achievements-card">
        <Title level={4}>科研成果统计</Title>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} md={6}>
            <div className="achievement-stat">
              <div className="stat-number">86</div>
              <div className="stat-label">国家级项目</div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="achievement-stat">
              <div className="stat-number">134</div>
              <div className="stat-label">省部级项目</div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="achievement-stat">
              <div className="stat-number">428</div>
              <div className="stat-label">高水平论文</div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div className="achievement-stat">
              <div className="stat-number">167</div>
              <div className="stat-label">授权专利</div>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default Research;